import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Settings, Sun, Moon, Layers, Grid2x2, X } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

export const PersonalizationPanel = () => {
  const { colorMode, variant, toggleColor, setVariant } = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-[120] flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            className="surface-card p-5 w-[280px] backdrop-blur-xl"
            style={{ background: "color-mix(in srgb, var(--surface) 82%, transparent)" }}
            data-testid="personalization-panel"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="eyebrow">Personalize</span>
              <button data-testid="close-panel-btn" onClick={() => setOpen(false)} aria-label="Close" className="opacity-60 hover:opacity-100 transition-opacity">
                <X size={16} />
              </button>
            </div>

            <p className="text-[0.65rem] uppercase tracking-widest mb-2" style={{ color: "var(--muted-t)" }}>Appearance</p>
            <button
              data-testid="toggle-color-mode"
              onClick={toggleColor}
              className="w-full flex items-center justify-between p-3 mb-4 rounded-lg hairline border transition-colors"
              style={{ background: "var(--surface-2)" }}
            >
              <span className="flex items-center gap-2 text-sm">
                {colorMode === "dark" ? <Moon size={16} /> : <Sun size={16} />}
                {colorMode === "dark" ? "Deep Charcoal" : "Stark White"}
              </span>
              <span className="text-xs" style={{ color: "var(--accent)" }}>Switch</span>
            </button>

            <p className="text-[0.65rem] uppercase tracking-widest mb-2" style={{ color: "var(--muted-t)" }}>Template Style</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "minimal", label: "Minimal", icon: Layers },
                { key: "structural", label: "Enterprise", icon: Grid2x2 },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  data-testid={`variant-${key}`}
                  onClick={() => setVariant(key)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-colors"
                  style={{
                    background: variant === key ? "var(--accent-soft)" : "var(--surface-2)",
                    borderColor: variant === key ? "var(--accent)" : "var(--line)",
                    color: variant === key ? "var(--accent)" : "var(--text)",
                  }}
                >
                  <Icon size={18} />
                  <span className="text-xs">{label}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="open-personalization-btn"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
        style={{ background: "var(--accent)", color: "var(--bg)" }}
        aria-label="Personalize site"
      >
        <motion.span animate={{ rotate: open ? 90 : 0 }} transition={{ type: "spring", stiffness: 200 }}>
          <Settings size={22} />
        </motion.span>
      </motion.button>
    </div>
  );
};

export default PersonalizationPanel;
